import { Injectable, OnDestroy } from '@angular/core';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { environment } from '@env/environment';
import { CompaniesService } from './companies.service';
import { UserModel } from '@data-layer/models/user';
import { ReplaySubject, Subject } from 'rxjs';
import { ApplicationModeService } from './application-mode.service';
import { enumApplicationMode } from '@enum/enumApplicationMode';
import { GoogleAnalyticsService } from './google-analytics.service';
import { enumGATagEventLabel } from '@enum/enumGATagEventLabel';
import { isEqual, pick } from 'lodash-es';

declare let fcWidget: any;

@Injectable({ providedIn: 'root' })
export class WidgetsService implements OnDestroy {
  public onWidgetReload: Subject<any> = new Subject();

  private timer: any;

  private excludeUrls: string[] = ['/auth/lcl-get-quote'];

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private companiesService: CompaniesService,
    private applicationModeService: ApplicationModeService,
    private googleAnalyticsService: GoogleAnalyticsService,
  ) {
    if (typeof fcWidget === 'undefined') {
      const freshchatScript = document.getElementById('freshchatScript');
      if (freshchatScript) {
        freshchatScript.onload = () => this.initWidgetsByActiveMode(this.applicationModeService.activeApplicationMode);
      }
    }
    this.applicationModeService.onActiveApplicationModeChange$.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(() => {
      this.initWidgetsByActiveMode(this.applicationModeService.activeApplicationMode);
    });
    this.companiesService.userChange$.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(({ current, previous }) => {
      const fieldsToCompare = ['firstName', 'lastName', 'email', 'phoneNumber'];
      const previousUserData: Partial<UserModel> = pick(previous, fieldsToCompare);
      const currentUserData: Partial<UserModel> = pick(current, fieldsToCompare);
      if (!isEqual(currentUserData, previousUserData)) {
        this.initWidgetsByActiveMode(this.applicationModeService.activeApplicationMode);
      }
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public initWidgetsByActiveMode(applicationMode: enumApplicationMode) {
    if (applicationMode === enumApplicationMode.SUPPORT) {
      this.destroyWidgets();
      return;
    }
    setTimeout(() => {
      this.initWidgets();
    }, 0);
  }

  public destroyWidgets() {
    this.destroyFcWidget();
  }

  public openWidget(tags: string[] = [], data?: { name?: string; replyText?: string }) {
    fcWidget.setTags(tags);

    if (!fcWidget.isOpen()) {
      fcWidget.open(data as any);
    }
  }

  public trackData(tackName: string, data: { [name: string]: string }) {
    fcWidget.track(tackName, data);
  }

  public setWidgetBelow(): void {
    const widget: HTMLElement = document.getElementById('fc_frame');
    widget.style.zIndex = null;
  }

  public setWidgetUnder(): void {
    const widget: HTMLElement = document.getElementById('fc_frame');
    widget.style.zIndex = '-1';
  }

  private initWidgets() {
    if (typeof fcWidget === 'undefined' || this.excludeUrls.includes(location.pathname)) {
      return;
    }
    if (fcWidget.isInitialized()) {
      fcWidget.destroy();
      this.waitUntilDestroyed(() => {
        this.initFcWidget();
      });
    } else {
      this.initFcWidget();
    }
  }

  private waitUntilDestroyed(callback: () => void) {
    if (this.timer) {
      clearTimeout(this.timer);
    }
    if (fcWidget.isInitialized()) {
      this.timer = setTimeout(() => {
        this.waitUntilDestroyed(callback);
      }, 100);
    } else {
      if (callback) {
        callback();
      }
    }
  }

  private initFcWidget() {
    const user = this.companiesService.getUser();
    fcWidget.init({
      token: environment.freshchat.token,
      host: environment.freshchat.host,
      externalId: user && user.uid ? user.uid : undefined,
      restoreId: user && user.freshchatRestoreId ? user.freshchatRestoreId : undefined,
      firstName: user && user.firstName ? user.firstName : undefined,
      lastName: user && user.lastName ? user.lastName : undefined,
      phone: user && user.phoneNumber ? user.phoneNumber : undefined,
      email: user && user.email ? user.email : undefined,
    });

    this.onWidgetReload.next(false);
    fcWidget.on('widget:loaded', () => {
      this.onWidgetReload.next(true);
      this.setUserData();

      fcWidget.on('widget:opened', () => {
        this.googleAnalyticsService.freshchatEvent(enumGATagEventLabel.WidgetOpen);
      });
      fcWidget.on('widget:closed', () => {
        this.googleAnalyticsService.freshchatEvent(enumGATagEventLabel.WidgetClose);
      });
      fcWidget.on('message:received', () => {
        this.googleAnalyticsService.freshchatEvent(enumGATagEventLabel.Received);
      });
      fcWidget.on('message:sent', () => {
        this.googleAnalyticsService.freshchatEvent(enumGATagEventLabel.Sent);
      });
      fcWidget.on('user:statechange', (resp) => {
        if (resp && resp.data && resp.data.userState === 'created') {
          this.googleAnalyticsService.freshchatEvent(enumGATagEventLabel.UserCreate);
        }
        if (resp && resp.data && resp.data.userState === 'loaded') {
          this.googleAnalyticsService.freshchatEvent(enumGATagEventLabel.UserLoaded);
        }
        if (resp && resp.data && resp.data.userState === 'identified') {
          this.googleAnalyticsService.freshchatEvent(enumGATagEventLabel.UserIdentified);
        }
        if (resp && resp.data && resp.data.userState === 'restored') {
          this.googleAnalyticsService.freshchatEvent(enumGATagEventLabel.UserRestored);
        }
      });
    });
  }

  private destroyFcWidget() {
    if (typeof fcWidget === 'undefined') {
      return;
    }
    fcWidget.user.clear();
    fcWidget.destroy();
  }

  private setUserData() {
    const user = this.companiesService.getUser();
    if (!user) {
      fcWidget.user.clear();
      return;
    }
    fcWidget.user.get((resp) => {
      const status = resp && resp.status;
      if (status !== 200) {
        // To set unique user id in your system when it is available
        fcWidget.setExternalId(user.uid);

        this.setUserProperties(user);

        fcWidget.on('user:created', (createdUserResp) => {
          const createdUserStatus = createdUserResp && createdUserResp.status,
            createdUserData = createdUserResp && createdUserResp.data;
          if (createdUserStatus === 200) {
            if (createdUserData.restoreId) {
              this.companiesService
                .updateUser({ ...user, freshchatRestoreId: createdUserData.restoreId })
                .pipe(takeUntil(this.destroy$))
                .subscribe();
            }
          }
        });
      } else {
        this.updateUserUnmatchedInfo(resp.data, user);
      }
    });
  }

  private setUserProperties(user: UserModel) {
    fcWidget.user.setProperties({
      externalId: user && user.uid ? user.uid : undefined,
      restoreId: user && user.freshchatRestoreId ? user.freshchatRestoreId : undefined,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phoneNumber,
    });
  }

  private updateUserUnmatchedInfo(
    freshchatUser: { email?: string; firstName?: string; lastName?: string; phone?: string },
    user: UserModel,
  ) {
    const updateUserInfo: { email?: string; firstName?: string; lastName?: string; phone?: string } = {};
    if (user.firstName !== freshchatUser.firstName) {
      updateUserInfo.firstName = user.firstName;
    }
    if (user.lastName !== freshchatUser.lastName) {
      updateUserInfo.lastName = user.lastName;
    }
    if (user.email !== freshchatUser.email) {
      updateUserInfo.email = user.email;
    }
    if (user.phoneNumber !== freshchatUser.phone) {
      updateUserInfo.phone = user.phoneNumber;
    }

    if (Object.keys(updateUserInfo).length) {
      fcWidget.user.setProperties(updateUserInfo);
    }
  }
}
